import React, { useState } from 'react';
import { Box, Typography, Button } from "@mui/material";
import { DndProvider, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import ProductivityChat from "../../components/ProductivityChatWindows/ProductivityChat";
import TaskButton from "../../components/TaskButtons/TaskButton";
import figAgentLogo from "../../assets/fig_agent.png";
import figLogo from "../../assets/fig_CROP.png"; // FIG logo

const ItemType = "task";

const Sandbox = () => {
    const [droppedTasks, setDroppedTasks] = useState([]);

    // Drop zone logic
    const [, drop] = useDrop({
        accept: ItemType,
        drop: (draggedItem) => {
            setDroppedTasks(prev => {
                const newId = draggedItem.id || `task-${Date.now()}`;
                return [...prev, { id: newId, content: draggedItem.content }]; // Add new dropped task
            });
        }
    });

    // Delete task handler
    const handleDeleteTask = (taskId) => {
        setDroppedTasks(prev => prev.filter(task => task.id !== taskId));
    };

    // Function to open the link in an 800x800 new window
    const openRequestForm = () => {
        window.open(
            "https://app.smartsheet.com/b/form/3507048c22ba47c196575bfc27d8407a",
            "_blank",
            "width=800,height=800,top=100,left=100"
        );
    };

    return (
        <DndProvider backend={HTML5Backend}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100vh",
                    backgroundColor: "#f9f9f9",
                }}
            >
                {/* Instructions Section (Centered at the Top) */}
                <Box
                    sx={{
                        backgroundColor: "#f0f0f0",
                        padding: 2,
                        borderBottom: "2px solid #ccc",
                        textAlign: "center",
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: "bold",
                            marginBottom: 1,
                            color: "#333",
                        }}
                    >
                        Sandbox Instructions
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: "#555",
                        }}
                    >
                        Drag responses from the <strong>Productivity Chat</strong> into the drop area below.
                        You can <strong>grade</strong> or <strong>delete</strong> them for later use.
                    </Typography>
                </Box>

                {/* Large "Feature or Data Training Request" Button */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        padding: 3,
                        backgroundColor: "#ffffff",
                        borderBottom: "2px solid #ccc",
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={openRequestForm}
                        sx={{
                            backgroundColor: "#008080", // FIG Blue-Green
                            color: "#ffffff",
                            padding: "15px 30px",
                            fontSize: "20px",
                            fontWeight: "bold",
                            textTransform: "none",
                            borderRadius: "25px",
                            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            "&:hover": {
                                backgroundColor: "#006666",
                                boxShadow: "0px 6px 15px rgba(0, 0, 0, 0.3)",
                            },
                        }}
                    >
                        <Box
                            component="img"
                            src={figLogo}
                            alt="FIG Logo"
                            sx={{
                                width: "30px",
                                height: "30px",
                            }}
                        />
                        Feature or Data Training Request
                    </Button>
                </Box>

                {/* Main Content (Chat + Drop Area) */}
                <Box
                    sx={{
                        display: "flex",
                        flex: 1,
                        overflow: "hidden",
                    }}
                >
                    {/* Left Section: Productivity Chat (1/3 Width) */}
                    <Box
                        sx={{
                            width: "33.33%",
                            padding: 3,
                            backgroundColor: "#eaeaea",
                            borderRight: "2px solid #ccc",
                            overflowY: "auto",
                        }}
                    >
                        <ProductivityChat />
                    </Box>

                    {/* Right Section: Drop Area (2/3 Width) */}
                    <Box
                        ref={drop}
                        sx={{
                            flex: 1,
                            backgroundColor: "#fff",
                            padding: 3,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: 2,
                            overflowY: "auto",
                            position: "relative",
                        }}
                    >
                        {/* FIG Agent Logo as Round Watermark */}
                        <Box
                            component="img"
                            src={figAgentLogo}
                            alt="FIG Agent Logo Watermark"
                            sx={{
                                position: "absolute",
                                width: "500px",
                                height: "500px",
                                borderRadius: "50%", // Make it round
                                opacity: 0.08,
                                left: "50%",
                                top: "50%",
                                transform: "translate(-50%, -50%)",
                                pointerEvents: "none",
                            }}
                        />

                        {/* Dropped Tasks */}
                        {droppedTasks.map(task => (
                            <Box
                                key={task.id}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                    width: "100%",
                                    border: "1px solid #ccc",
                                    borderRadius: 1,
                                    padding: 1,
                                    backgroundColor: "#f9f9f9",
                                }}
                            >
                                <TaskButton content={task.content} />
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    size="small"
                                    onClick={() => handleDeleteTask(task.id)}
                                >
                                    Delete
                                </Button>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        </DndProvider>
    );
};

export default Sandbox;
